import React, { useState, useEffect } from 'react';
import { Lock, Key, Eye, AlertCircle, Info, ShieldCheck } from 'lucide-react';
import { Text } from './Text/Text';
import { Button } from './Button/Button';
import { Input } from './Input/Input';

interface CredentialParameter {
  key_name: string;
  display_name: string;
  value_type: 'secure' | 'plain_text';
  description: string;
  required: boolean;
}

interface CredentialCaptureModalProps {
  visible: boolean;
  requestId: string;
  parameters: CredentialParameter[];
  envFilePath: string;
  onSubmit: (requestId: string, values: Record<string, string>) => void;
  onCancel: () => void;
}

export const CredentialCaptureModal: React.FC<CredentialCaptureModalProps> = ({
  visible,
  requestId,
  parameters,
  envFilePath,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (visible) {
      // Reset form when modal opens
      const initialData: Record<string, string> = {};
      parameters.forEach(param => {
        initialData[param.key_name] = '';
      });
      setFormData(initialData);
      setValidationErrors({}); // Clear any validation errors
    }
  }, [visible, parameters]);

  const handleInputChange = (keyName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [keyName]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[keyName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[keyName];
        return newErrors;
      });
    }
  };

  // Validation function
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    parameters.forEach(param => {
      if (param.required) {
        const value = formData[param.key_name];
        if (!value || value.trim() === '') {
          errors[param.key_name] = `${param.display_name} is required`;
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Check if form has all required fields filled
  const isFormValidForSubmission = (): boolean => {
    return parameters.every(param => {
      if (param.required) {
        const value = formData[param.key_name];
        return value && value.trim() !== '';
      }
      return true;
    });
  };

  const handleSubmit = async () => {
    // Validate form before submission
    if (!validateForm()) {
      console.log('❌ Form validation failed, not submitting');
      return;
    }

    try {
      setLoading(true);
      
      // Create credentials object with all parameters (including blank values)
      const credentials: Record<string, string> = {};
      parameters.forEach(param => {
        const value = formData[param.key_name];
        credentials[param.key_name] = value ? value.trim() : '';
      });
      
      await onSubmit(requestId, credentials);
    } catch (error) {
      console.error('❌ Credential submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setLoading(true);
      
      // Create credentials object with all blank values
      const credentials: Record<string, string> = {};
      parameters.forEach(param => {
        credentials[param.key_name] = ''; // Explicitly set to blank
      });
      
      // Add a special flag to indicate this was a deliberate skip
      credentials['__skipped_by_user'] = 'true';
      
      await onSubmit(requestId, credentials);
    } catch (error) {
      console.error('❌ Credential skip failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInputComponent = (param: CredentialParameter) => {
    const hasError = validationErrors[param.key_name];
    const value = formData[param.key_name] || '';
    
    const placeholder = param.required 
      ? `Enter your ${param.display_name.toLowerCase()} (required)`
      : `Enter your ${param.display_name.toLowerCase()} (optional)`;

    return (
      <Input
        type={param.value_type === 'secure' ? 'password' : 'text'}
        value={value}
        onChange={(e) => handleInputChange(param.key_name, e.target.value)}
        placeholder={placeholder}
        errorText={hasError ? validationErrors[param.key_name] : undefined}
        size="md"
        autoComplete="off"
        className={param.value_type === 'secure' ? 'font-mono' : ''}
      />
    );
  };

  const getIcon = (valueType: string) => {
    return valueType === 'secure' ? (
      <Lock className="w-4 h-4 text-text-brand-primary" />
    ) : (
      <Eye className="w-4 h-4 text-text-secondary" />
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-alpha-black-70 flex items-center justify-center z-50 p-bg-v2-lg">
      <div className="bg-bg-primary rounded-bg-v2-xl shadow-bg-v2-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-bg-v2-2xl border-b border-border-secondary">
          <div className="flex items-center gap-bg-v2-md">
            <Key className="w-6 h-6 text-text-brand-primary" />
            <Text variant="text-lg" weight="semibold" color="primary">
              Credential Configuration Required
            </Text>
          </div>
        </div>

        {/* Content */}
        <div className="p-bg-v2-2xl overflow-y-auto flex-1">
          {/* Info Banner */}
          <div className="bg-bg-secondary border border-border-secondary rounded-bg-v2-md p-bg-v2-lg mb-bg-v2-xl">
            <div className="flex items-start gap-bg-v2-md">
              <Info className="w-5 h-5 text-text-brand-primary flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-bg-v2-xs">
                <Text variant="text-sm" weight="semibold" color="primary">
                  Environment File: {envFilePath}
                </Text>
                <Text variant="text-sm" weight="regular" color="secondary">
                  Please provide the following credentials to enable full functionality, or skip to continue without them.
                </Text>
              </div>
            </div>
          </div>

          {/* Warning Banner */}
          <div className="bg-bg-warning-secondary border border-utility-warning-300 rounded-bg-v2-md p-bg-v2-lg mb-bg-v2-xl">
            <div className="flex items-start gap-bg-v2-md">
              <AlertCircle className="w-5 h-5 text-text-warning-primary flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-bg-v2-xs">
                <Text variant="text-sm" weight="semibold" color="primary">
                  Important Notice
                </Text>
                <Text variant="text-sm" weight="regular" color="secondary">
                  Leaving credentials blank may result in non-functional features. You can fill these values later through the credential management interface if needed.
                </Text>
              </div>
            </div>
          </div>

          {/* Credential Fields */}
          <div className="flex flex-col gap-bg-v2-xl">
            {parameters.map((param, index) => (
              <div key={param.key_name} className="flex flex-col gap-bg-v2-md">
                {/* Field Header */}
                <div className="flex items-center gap-bg-v2-sm">
                  {getIcon(param.value_type)}
                  <Text variant="text-sm" weight="medium" color="primary">
                    {param.display_name}
                    {param.required && <span className="text-text-error-primary ml-1">*</span>}
                  </Text>
                </div>
                
                {/* Field Description */}
                <Text variant="text-sm" weight="regular" color="secondary">
                  {param.description}
                </Text>
                
                {/* Input Field */}
                {getInputComponent(param)}
                
                {/* Environment Variable Name */}
                <div className="flex items-center gap-bg-v2-xs">
                  <Text variant="text-xs" weight="medium" color="tertiary">
                    Environment Variable:
                  </Text>
                  <Text variant="text-xs" weight="regular" color="tertiary" className="font-mono">
                    {param.key_name}
                  </Text>
                </div>
              </div>
            ))}
          </div>

          {/* Security Notice */}
          <div className="bg-bg-success-secondary border border-utility-success-300 rounded-bg-v2-md p-bg-v2-lg mt-bg-v2-xl">
            <div className="flex items-start gap-bg-v2-md">
              <ShieldCheck className="w-5 h-5 text-text-success-primary flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-bg-v2-xs">
                <Text variant="text-sm" weight="semibold" color="success">
                  Security Note
                </Text>
                <Text variant="text-sm" weight="regular" color="secondary">
                  These values will be encrypted and stored securely. They will be written to your project's environment file for use in your application.
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-bg-v2-2xl border-t border-border-secondary gap-bg-v2-lg">
          <div className="flex items-center gap-bg-v2-xs flex-1">
            <Info className="w-4 h-4 text-text-tertiary flex-shrink-0" />
            <Text variant="text-xs" weight="regular" color="tertiary">
              You can manage these credentials later in the project settings
            </Text>
          </div>
          <div className="flex gap-bg-v2-md">
            <Button
              label={loading ? 'Skipping...' : 'Skip for Now'}
              variant="outline"
              size="medium"
              onClick={handleSkip}
              disabled={loading}
            />
            <Button
              label={loading ? 'Submitting...' : 'Submit Credentials'}
              variant="primary"
              size="medium"
              onClick={handleSubmit}
              disabled={loading || !isFormValidForSubmission()}
              loading={loading}
              title={!isFormValidForSubmission() ? 'Please fill in all required fields' : ''}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialCaptureModal;